import {
  Avatar,
  Box,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Progress,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import {
  getCourseEnrolmentHealthByLabel,
  getProgressStatusText,
} from '@duke-hq/shared-constants/enrolments';
import { calculateEnrolmentMetrics } from '@duke-hq/svc-api/lib/enrolment/calculateEnrolmentMetrics';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { djs } from '@duke-hq/djs';
import { Ellipsis, Info, SquarePen } from 'lucide-react';
import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EnrolmentHealthBadge } from '@duke-hq/ui/components/EnrolmentHealthBadge/EnrolmentHealthBadge';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { SortOrder, SortOrderSchema } from '@duke-hq/lib/pagination';
function EnrolmentListRow({
  enrolment,
  onClick,
}: {
  enrolment: NonNullable<RouterOutputs['enrolments']['all']>['items'][number];
  onClick?: (id: number) => void;
}) {
  const fullName = `${enrolment.user.first_name} ${enrolment.user.last_name}`;
  const { currentSemester, expectedProgress, startDelay, targetDate } =
    calculateEnrolmentMetrics(enrolment);
  const progressHealthColorScheme = enrolment.progress_health
    ? getCourseEnrolmentHealthByLabel(enrolment.progress_health)?.colorScheme
    : undefined;
  const progressStatusText = getProgressStatusText(enrolment.progress_status);
  const isAtRiskOrOverdue = enrolment.progress_health
    ? ['At risk', 'Overdue'].includes(enrolment.progress_health)
    : false;

  return (
    <Tr
      onClick={() => {
        if (onClick) {
          onClick(enrolment.id);
        }
      }}
      cursor="pointer"
      _hover={{ bg: 'bg.subtle' }}
    >
      <Td>
        <HStack spacing="3">
          <Flex alignItems="center">
            <Avatar
              size="sm"
              mr={'12px'}
              name={fullName}
              src={enrolment.user?.picture_url ?? undefined}
            ></Avatar>
            <Box>
              <Text fontWeight="medium">{fullName}</Text>
              <Text fontWeight="normal" color="fg.muted">
                {enrolment.user.email}
              </Text>
            </Box>
          </Flex>
        </HStack>
      </Td>
      <Td style={{ maxWidth: '300px' }}>
        {enrolment.course ? (
          <>
            <Text isTruncated fontWeight="medium" title={enrolment.course.title}>
              {enrolment.course.title}
            </Text>
            {enrolment.course.identifier ? (
              <Text color="fg.muted">{enrolment.course.identifier}</Text>
            ) : null}
          </>
        ) : null}
      </Td>
      <Td>
        {progressStatusText ? (
          <Tag
            fontSize="xs"
            fontWeight="bold"
            borderRadius="full"
            colorScheme={progressStatusText.colorScheme}
            variant={progressStatusText.variant}
          >
            {progressStatusText.text}
          </Tag>
        ) : (
          <Flex gap="5px" alignItems="center">
            <Box position="relative" rounded={12} overflow="hidden" height="22px" width="100px">
              <Progress
                value={currentSemester === 0 ? 0 : enrolment.semesterProgress}
                rounded={12}
                height="100%"
                width="100%"
                sx={{
                  '& > div': {
                    background: 'blue.200',
                    rounded: 0,
                  },
                }}
                borderColor="blue.500"
                borderWidth={1}
              />

              <Box
                borderRight="1px"
                borderColor={isAtRiskOrOverdue ? 'red' : 'info'}
                borderStyle="dashed"
                position="absolute"
                top={0}
                height="22px"
                left={`${expectedProgress}%`}
              />
            </Box>

            <Text
              size="sm"
              fontWeight="semibold"
              color={isAtRiskOrOverdue ? `${progressHealthColorScheme}.500` : undefined}
            >
              {currentSemester === 0 ? 0 : enrolment.semesterProgress}%
            </Text>
          </Flex>
        )}
      </Td>
      <Td>
        <EnrolmentHealthBadge health={enrolment.health} />
      </Td>
      <Td>
        {enrolment.user.last_activity_at ? (
          <Text color="fg.muted">{djs().to(djs(enrolment.user.last_activity_at))}</Text>
        ) : (
          <Text color="fg.muted">
            <Trans>Not logged in yet</Trans>
          </Text>
        )}
      </Td>
      <Td color={isAtRiskOrOverdue ? 'red' : 'fg.muted'}>
        {startDelay > 0 ? t`${startDelay} days` : <Trans>none</Trans>}
      </Td>
      <Td>{djs(enrolment.start_at_date).format('DD MMM YY')}</Td>
      <Td>{targetDate ? targetDate.medium : <Trans>none</Trans>}</Td>
      <Td>
        {enrolment?.enrolment_contract_type ? (
          <Text>{enrolment.enrolment_contract_type}</Text>
        ) : (
          <Text color="fg.muted">N/A</Text>
        )}
      </Td>

      <Td position="sticky" right={0} zIndex={1} bg="white" px={8}>
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          boxShadow="-4px 0px 6px rgba(0, 0, 0, 0.1)"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="Options"
              icon={<Ellipsis width="20px" height="20px" />}
              variant="ghost"
              size="sm"
              onClick={(e) => e.stopPropagation()}
              display="inline-flex"
            />
            <Portal>
              <MenuList>
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onClick) {
                      onClick(enrolment.id);
                    }
                  }}
                  icon={<Info width={16} height={16} />}
                >
                  <Trans>View details</Trans>
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </Box>
      </Td>
    </Tr>
  );
}

export function TeamEnrolmentsTable({
  enrolments = [],
}: {
  enrolments: NonNullable<RouterOutputs['enrolments']['all']>['items'];
}): React.ReactElement {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sort = SortOrderSchema.safeParse(searchParams.get('sort')).data;
  const orderBy = searchParams.get('orderBy') ?? undefined;

  const handleClick = useCallback(
    (id: string | number) => {
      navigate(`/team/enrolment/${id}?${searchParams.toString()}`);
    },
    [navigate, searchParams],
  );

  const handleSort = useCallback(
    (name: string) => {
      const newSortDirection = sort === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.set('orderBy', name);
      newParams.set('sort', newSortDirection);
      setSearchParams(newParams);
    },
    [sort, setSearchParams, searchParams],
  );

  return (
    <Table>
      <Thead>
        <Tr>
          <Th>
            <SortableHeader
              label={t`Student`}
              columnName="name"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Course`}
              columnName="course_id"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <Trans>Progress</Trans>
          </Th>
          <Th>
            <SortableHeader
              label={t`Health`}
              columnName="progress_health"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Last activity`}
              columnName="last_activity_at"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <Trans>Start Delay</Trans>
          </Th>
          <Th>
            <SortableHeader
              label={t`Course Start`}
              columnName="start_at_date"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <Trans>Course Target</Trans>
          </Th>
          <Th>
            <SortableHeader
              label={t`Contract`}
              columnName="enrolment_contract_type"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th position="sticky" right={0} zIndex={2} bg="white !important" px={8}>
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              boxShadow="-4px 0px 6px rgba(0, 0, 0, 0.1)"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <SquarePen width={20} height={20} />
            </Box>
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {Array.isArray(enrolments) && enrolments.length
          ? enrolments.map((enrolment) => (
              <EnrolmentListRow key={enrolment.id} enrolment={enrolment} onClick={handleClick} />
            ))
          : null}
      </Tbody>
    </Table>
  );
}
